/**
 * Maps the API v2 London artifact (`public/map-v2/schema.json`) onto the
 * `SchemaData` shape every web consumer reads — the app (map tap regions,
 * night filter, route overlay, routing graph, station card and page, search)
 * and the prerender scripts (station, line and route pages, sitemap).
 *
 * Plain ESM so Node build scripts and the Vite app share ONE adapter
 * (types in `mapSchemaV2.d.mts`, contract types in `src/schema/v2/types.ts`).
 *
 * The map's unit is the **stop** (line × station): a v2 stop becomes one
 * station record keyed by the stop id, as v1 kept one record per line at a
 * station. The NaPTAN station id goes to `naptanId`, so cluster logic
 * (favourites, carousel, physical-station dedup, station pages) groups the
 * records of one physical station. Data-driven: no line or station id
 * appears here. Port of iOS `MapSchemaV2SnapshotAdapter` and Android
 * `MapSchemaV2Adapter` (TASK-986); unlike the mobile ports it keeps the SVG
 * element strings, which the web route overlay draws from.
 */
export function adaptSchemaV2(schema) {
  const lines = new Map(schema.lines.map((l) => [l.id, line(l)]))
  const stationsById = new Map(schema.stations.map((s) => [s.id, s]))
  const neighbours = transferNeighbours(schema.transfers)
  const stations = []
  const tapGrids = []
  for (const stop of schema.stops) {
    // A dangling reference fails the server build (validate §4); skipping
    // keeps a malformed artifact from breaking the whole map.
    const stopLine = lines.get(stop.lineId)
    const station = stationsById.get(stop.stationId)
    if (stopLine === undefined || station === undefined) continue
    const coordinate = station.coordinate ?? null
    stations.push({
      id: stop.id,
      naptanId: stop.stationId,
      slug: station.slug ?? undefined,
      primaryLineId: station.primaryLineId ?? undefined,
      localName: station.name,
      x: stop.x,
      y: stop.y,
      coordinate:
        coordinate === null ? undefined : { latitude: coordinate.lat, longitude: coordinate.lon },
      svgPath: stop.paths.light,
      modesSvg: modesSvg(stop.paths),
      // Late-night frame position (TASK-1136): only where it moves the stop.
      ...(stop.night == null ? {} : { night: { x: stop.night.x, y: stop.night.y } }),
      line: stopLine,
      zone: station.zone ?? null,
      facilities: station.facilities ?? [],
      transferStationsIds: neighbours.get(stop.id) ?? [],
      stationTimetable: stop.timetable ?? undefined,
      modes: known(stop.modes),
    })
    const nightGrid = stop.night?.tapGrid ?? null
    const grid = stop.tapGrid ?? nightGrid
    if (grid != null) {
      tapGrids.push({
        id: stop.id,
        size: { width: grid.width, height: grid.height },
        origin: { x: grid.x, y: grid.y },
        stationId: stop.id,
        modes: known(stop.modes),
        ...(nightGrid == null ? {} : { night: rect(nightGrid) }),
      })
    }
  }
  return {
    version: schema.version,
    stations,
    tapGrids,
    connections: schema.connections.map((c) => ({
      id: c.id,
      lineId: c.lineId,
      svgPath: c.paths.light,
      modesSvg: modesSvg(c.paths),
      weight: c.seconds,
      fromStationId: c.from,
      toStationId: c.to,
      // A v2 edge runs both ways unless `oneWay` (a Figma direction arrow:
      // ridden from → to only).
      reversible: c.oneWay !== true,
      modes: known(c.modes),
      forwardBound: c.forwardBound ?? undefined,
      reverseBound: c.reverseBound ?? undefined,
    })),
    transitions: schema.transfers.map((t) => ({
      id: t.id,
      fromStationId: t.from,
      toStationId: t.to,
      // Street interchanges are not drawn: their paths are empty.
      svgPath: t.paths.light === '' ? undefined : t.paths.light,
      modesSvg: t.paths.light === '' && t.paths.dark === '' ? undefined : modesSvg(t.paths),
      skipInitialDraw: t.skip,
      weight: t.seconds,
      street: t.street,
      reversible: true,
      convenientTransferCars: (t.convenientCars ?? []).map((c) => ({
        prevStationId: c.prevStopId,
        toStationId: c.toStopId,
        types: c.types,
      })),
      modes: known(t.modes),
    })),
    texts: schema.labels.map((l) => ({
      id: l.id,
      svgPath: l.paths.light,
      modesSvg: modesSvg(l.paths),
      skipInitialDraw: l.skip,
      stationId: l.stopId ?? undefined,
      modes: known(l.modes),
    })),
    additional: schema.additionals.map((a) => ({
      id: a.id,
      svgPath: a.paths.light,
      modesSvg: modesSvg(a.paths),
      modes: known(a.modes),
      // A direction arrow names its one-way connection; the route
      // highlight draws it with that connection.
      connectionId: a.connectionId ?? undefined,
    })),
  }
}

function rect(r) {
  return { size: { width: r.width, height: r.height }, origin: { x: r.x, y: r.y } }
}

function line(l) {
  return {
    id: l.id,
    slug: l.slug ?? undefined,
    localName: l.name,
    shortLocalName: l.shortName ?? undefined,
    // An unknown transport family stays as is; consumers compare against
    // the families they know and fall back to the common style otherwise.
    type: l.type,
    hexColor: l.colors.light,
    colorModes: { light: l.colors.light, dark: l.colors.dark },
  }
}

/** Per-theme markup, plus the late-night frame's where it draws the element (TASK-1136). */
function modesSvg(paths) {
  const out = { light: paths.light, dark: paths.dark }
  if (typeof paths.night === 'string' && paths.night !== '') out.night = paths.night
  return out
}

/** v1-shaped `transferStationsIds`: stops one transfer away, both directions, sorted. */
function transferNeighbours(transfers) {
  const sets = new Map()
  const add = (from, to) => {
    const set = sets.get(from) ?? new Set()
    set.add(to)
    sets.set(from, set)
  }
  for (const t of transfers) {
    add(t.from, t.to)
    add(t.to, t.from)
  }
  return new Map([...sets].map(([id, set]) => [id, [...set].sort()]))
}

/** Modes the client knows; a new mode is dropped rather than misread. */
function known(modes) {
  return modes.filter((m) => m === 'normal' || m === 'night')
}
